import { DateTime } from 'luxon'

import { Language } from '@/core/api/dtos/common/language'

import { Segment } from './segment'

export enum RadioType {
	COMMON = 'common',
	SMART_FOOT_TRAFFIC = 'smart_foot_traffic',
	SMART_WEATHER = 'smart_weather',
	SMART_FOOT_TRAFFIC_OCCUPATION = 'smart_foot_traffic_occupation',
}

export class Radio {
	public id: number
	public title: string
	public titleTranslations: Language
	public description: string
	public descriptionTranslations: Language
	public categories: string
	public genres: string | null
	public businessCategoriesIds: number[]
	public cover: string
	public thumbs: string[]
	public public: boolean
	public type: string
	public rotateAutomatically: boolean
	public rotationPercentage: number
	public rotationPeriodAmount: number
	public rotationPeriodType: string
	public nextRotationDate: DateTime
	public fromParent?: boolean
	public recommended?: boolean
	public reviewed?: boolean | null
	public reviewedBy?: number | null
	public reviewedAt?: DateTime | null
	public favorite?: boolean | null
	public createdAt: DateTime
	public updatedAt: DateTime
	public metadata?: {
		id?: number
		mixID?: number
		mixPercentage: number
		isDefault: boolean
		rrule: string | null
	}

	public segments?: Segment[]

	constructor(data: {
		id: number
		title: string
		titleTranslations: Language
		description: string
		descriptionTranslations: Language
		categories: string
		genres: string | null
		businessCategoriesIds: number[]
		cover: string
		thumbs: string[]
		public: boolean
		type: string
		rotateAutomatically: boolean
		rotationPercentage: number
		rotationPeriodAmount: number
		rotationPeriodType: string
		nextRotationDate: DateTime
		fromParent?: boolean
		recommended?: boolean
		reviewed?: boolean | null
		reviewedBy?: number | null
		reviewedAt?: DateTime | null
		favorite?: boolean | null
		createdAt: DateTime
		updatedAt: DateTime
		metadata?: {
			id?: number
			mixID?: number
			mixPercentage: number
			isDefault: boolean
			rrule: string | null
		}
		segments?: Segment[]
	}) {
		this.id = data.id
		this.title = data.title
		this.titleTranslations = data.titleTranslations
		this.description = data.description
		this.descriptionTranslations = data.descriptionTranslations
		this.categories = data.categories
		this.genres = data.genres
		this.businessCategoriesIds = data.businessCategoriesIds
		this.cover = data.cover
		this.thumbs = data.thumbs
		this.public = data.public
		this.type = data.type
		this.rotateAutomatically = data.rotateAutomatically
		this.rotationPercentage = data.rotationPercentage
		this.rotationPeriodAmount = data.rotationPeriodAmount
		this.rotationPeriodType = data.rotationPeriodType
		this.nextRotationDate = data.nextRotationDate
		this.fromParent = data.fromParent
		this.recommended = data.recommended
		this.reviewed = data.reviewed
		this.reviewedBy = data.reviewedBy
		this.reviewedAt = data.reviewedAt
		this.favorite = data.favorite
		this.createdAt = data.createdAt
		this.updatedAt = data.updatedAt
		this.metadata = data.metadata
		this.segments = data.segments
	}

	static create(data: Partial<Radio>): Radio {
		const defaultData = {
			id: 0,
			title: '',
			titleTranslations: {},
			description: '',
			descriptionTranslations: {},
			categories: '',
			genres: null,
			businessCategoriesIds: [],
			cover: '',
			thumbs: [],
			public: false,
			type: '',
			rotateAutomatically: false,
			rotationPercentage: 0,
			rotationPeriodAmount: 0,
			rotationPeriodType: '',
			nextRotationDate: DateTime.now(),
			fromParent: false,
			recommended: false,
			reviewed: false,
			reviewedBy: null,
			reviewedAt: null,
			favorite: false,
			createdAt: DateTime.now(),
			updatedAt: DateTime.now(),
		}

		return new Radio({
			...defaultData,
			...data,
		})
	}

	/**
	 * Clone the radio
	 */
	clone(): Radio {
		return new Radio({
			...this,
		})
	}

	/**
	 * Get the total number of songs in the radio
	 */
	getTotalSongs(): number {
		return (
			this.segments?.reduce((acc, segment) => acc + segment.songsCount, 0) ?? 0
		)
	}

	/**
	 * Get the total duration of the radio in seconds
	 */
	getTotalDuration(): number {
		return this.segments?.reduce((acc, segment) => acc + segment.length, 0) ?? 0
	}

	/**
	 * Return true if the radio is smart
	 */
	isSmart(): boolean {
		return this.type !== RadioType.COMMON
	}

	/**
	 * Return the label and icon of the radio for display
	 */
	getTypeForDisplay(): { label: string; icon: string } {
		if (this.isSmart()) {
			return { label: 'smart', icon: 'star' }
		}

		if (this.segments) {
			if (this.segments.length > 1) {
				return { label: 'schedule', icon: 'calendar-event' }
			}

			if (this.segments.length === 0) {
				return { label: 'playlist', icon: 'music-note-beamed' }
			}

			if (
				this.segments[0].startAt === '00:00:00' &&
				this.segments[0].endAt === '23:59:59'
			) {
				return { label: 'playlist', icon: 'music-note-beamed' }
			}
		}

		return { label: 'schedule', icon: 'calendar-event' }
	}
}
